import axios from 'axios';
import { Auth } from 'aws-amplify';
import { AmplifyEventBus } from 'aws-amplify-vue';
import * as Sentry from '@sentry/browser';
import store from '@/store/store';
import parseJWT from '@/utils/parseJWT';

function getUser() {
  try {
    // check if user is already saved in store
    if (store.state.userStore.user) {
      return store.state.userStore.user;
    }
    // otherwise we need to validate the token from localStorage
    const { idToken } = JSON.parse(localStorage.getItem('Exporo.Auth.SignedInUser')).signInUserSession;

    const options = {
      headers: {
        'Content-Type': 'application/json',
      },
    };

    return axios.post('https://auth.exporo.de/api/verify', idToken, options)
      .then(({ data }) => {
        if (data.signature) {
          // all good, set User in store
          data.payload.signInUserSession = {
            idToken: {
              jwtToken: idToken.jwt,
            },
          };

          data.payload.origin = 'OneLogin';

          Sentry.setUser({
            id: data.payload.sub,
            username: data.payload.name,
            email: data.payload.email,
          });

          store.commit('setUser', data.payload);

          return data.payload;
        }

        return null;
      })
      .catch((err) => {
        localStorage.removeItem('Exporo.Auth.SignedInUser');
        window.location.reload();
        console.error(err);
        throw err;
      });
  } catch (exception) {
    // user might be from cognito so let amplify deal with validations

    return Auth.currentAuthenticatedUser()
      .then((user) => {
        if (user && user.signInUserSession) {
          // GET APPLICATION CLIENT ID FROM USERNAME CONVENTION
          user.application_client_id = user.username.substr(user.username.length - 1);
          user.origin = 'Cognito';
          store.commit('setUser', user);

          return user;
        }
        // User needs to authenticate
        store.commit('setUser', null);

        return null;
      }).catch((err) => {
        console.error(err);
        // User needs to authenticate

        store.commit('setUser', null);

        return null;
      });
  }
}

function setOneLoginUser(data) {
  const user = {
    // used in requests headers bearer config
    signInUserSession: {
      idToken: {
        jwt: data.id_token,
      },
    },
    ...parseJWT(data.id_token),
    origin: 'OneLogin',
  };

  localStorage.setItem('Exporo.Auth.SignedInUser', JSON.stringify(user));
}

async function signUp(user) {
  try {
    const data = await Auth.signUp(user);

    if (data.userConfirmed === false) {
      AmplifyEventBus.$emit('authState', 'confirmSignUp');
    } else {
      AmplifyEventBus.$emit('authState', 'signIn');
    }

    return data;
  } catch (err) {
    throw new Error(err.message);
  }
}

async function confirmSignUp(username, code) {
  try {
    const data = await Auth.confirmSignUp(username, code);
    AmplifyEventBus.$emit('authState', 'signIn');

    return data;
  } catch (err) {
    throw new Error(err.message);
  }
}

async function resendSignUp(username) {
  try {
    await Auth.resendSignUp(username);

    return 'SUCCESS';
  } catch (err) {
    throw new Error(err.message);
  }
}

async function forgotPassword(username) {
  try {
    await Auth.forgotPassword(username);

    return 'SUCCESS';
  } catch (err) {
    throw new Error(err.message);
  }
}

async function forgotPasswordSubmit(username, code, password) {
  try {
    await Auth.forgotPasswordSubmit(username, code, password);
    AmplifyEventBus.$emit('authState', 'signIn');

    return 'SUCCESS';
  } catch (err) {
    throw new Error(err.message);
  }
}

async function signIn(username, password, newPassword, totpCode) {
  try {
    const user = await Auth.signIn(username, password);
    if (user) {
      // if user is added by admin, user should setup new password
      if (user.challengeName === 'NEW_PASSWORD_REQUIRED') {
        if (newPassword) {
          await Auth.completeNewPassword(user, newPassword);
        } else {
          return {
            type: 'NEW_PASSWORD_SETUP',
          };
        }
      }

      // if user has already set TOTP
      if (user.challengeName === 'SOFTWARE_TOKEN_MFA') {
        if (totpCode) {
          await Auth.confirmSignIn(user, totpCode, 'SOFTWARE_TOKEN_MFA');
        } else {
          return {
            type: 'MFA_VERIFY',
          };
        }
      }

      const currentMFAType = await getPreferredMFA(user);

      // set up TOTP auth
      if (currentMFAType === 'NOMFA') {
        if (totpCode) {
          await Auth.verifyTotpToken(user, totpCode);
          await Auth.setPreferredMFA(user, 'TOTP');
        } else {
          const code = await Auth.setupTOTP(user);
          const data = `otpauth://totp/AWSCognito:${user.username}?secret=${code}&issuer=AWSCognito`;

          return {
            type: 'MFA_SETUP',
            data,
          };
        }
      }

      AmplifyEventBus.$emit('authState', 'signedIn');
    }

    return {
      type: 'SIGN_IN_SUCCESS',
    };
  } catch (err) {
    if (err.code === 'UserNotConfirmedException') {
      // The error happens if the user didn't finish the confirmation step when signing up
      // In this case you need to resend the code and confirm the user
      // About how to resend the code and confirm the user, please check the signUp part
      AmplifyEventBus.$emit('authState', 'confirmSignUp');
    } else if (err.code === 'PasswordResetRequiredException') {
      // The error happens when the password is reset in the Cognito console
      // In this case you need to call forgotPassword to reset the password
      // Please check the Forgot Password part.
      AmplifyEventBus.$emit('authState', 'forgotPassword');
    } else if (err.code === 'NotAuthorizedException') {
      // The error happens when the incorrect password is provided
      throw new Error('Incorrect password.');
    } else if (err.code === 'UserNotFoundException') {
      // The error happens when the supplied username/email does not exist in the Cognito user pool
      throw new Error('User does not exist.');
    } else {
      console.error(err);
      throw new Error(err.message || 'Unknow error.');
    }

    return null;
  }
}

async function signOut() {
  const SignedInUser = JSON.parse(localStorage.getItem('Exporo.Auth.SignedInUser'));
  if (SignedInUser && SignedInUser.origin === 'OneLogin') {
    localStorage.removeItem('Exporo.Auth.SignedInUser');
    // reload page to enable redirect back on login
    window.location.reload();
  }

  return Auth.signOut({ global: true })
    .then(() => {
      window.location.reload();
    })
    .catch((err) => {
      console.error(err);

      return err;
    });
}

function getPreferredMFA(user) {
  return Auth.getPreferredMFA(user, { bypassCache: false });
}

export {
  getUser,
  signUp,
  confirmSignUp,
  resendSignUp,
  signIn,
  signOut,
  forgotPassword,
  forgotPasswordSubmit,
  getPreferredMFA,
  setOneLoginUser,
};



// WEBPACK FOOTER //
// ./src/utils/auth.js